const API_BASE = '';  // same origin

let _selectedClinic: string | null = null;

/** Called by AuthProvider to keep the selected clinic in sync for API calls. */
export function setApiClinic(clinicId: string | null): void {
  _selectedClinic = clinicId;
}

/** Exposed so non-apiFetch callers (e.g. XHR uploads) can mirror the header set. */
export function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)hq_csrf=([^;]+)/);
  return match?.[1] ?? '';
}

/** Current selected clinic — `null` if no clinic is selected yet. */
export function getSelectedClinic(): string | null {
  return _selectedClinic;
}

/**
 * Session-expiry subscriber (registered by `AuthProvider`).
 *
 * Before this existed there was NO 401 handling anywhere in the client: every
 * non-`ok` response became a generic `ApiError` and callers rendered "failed to
 * load". A dead session was therefore indistinguishable from a flaky network, so
 * polling pages (Kiosks worst of all) kept hammering authenticated endpoints
 * forever — prod showed a steady ~14 401/hour all night from abandoned tabs.
 *
 * A typed module slot rather than a `window` event: the wiring stays greppable
 * and testable, and there is exactly one consumer by construction.
 * Plan: docs/plans/2026-07-31-prod-log-hygiene-and-timeouts.md (item 2).
 */
type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler | null = null;

/** Returns an unsubscribe so the provider can clean up on unmount. */
export function setSessionExpiredHandler(handler: SessionExpiredHandler): () => void {
  onSessionExpired = handler;
  return () => {
    if (onSessionExpired === handler) onSessionExpired = null;
  };
}

/**
 * The clinic's staff IP allowlist refused this request (ruby-hq `ipGate`,
 * migration 0173): the shell swaps the whole app for a "clinic network" page.
 * Its own slot beside the expiry one, same shape and reasons.
 * Plan: docs/plans/2026-10-07-well-staff-ip-allowlist.md.
 */
type IpRefusedHandler = (ip: string | null) => void;
let onIpRefused: IpRefusedHandler | null = null;

export function setIpRefusedHandler(handler: IpRefusedHandler): () => void {
  onIpRefused = handler;
  return () => {
    if (onIpRefused === handler) onIpRefused = null;
  };
}

/** The gate's refusal, by its `error` only. A refused SIGN-IN carries the
 *  sentence in `error` (and the code in `code`), so it stays on the login page
 *  instead of opening the network page. */
export function isIpRefusal(status: number, body: { error?: unknown }): boolean {
  return status === 403 && body.error === 'ip-not-allowed';
}

/**
 * Endpoints where a 401 means "those credentials were wrong", NOT "your session
 * died" — firing the expiry handler here would be a self-inflicted logout loop
 * on every mistyped password.
 */
const AUTH_CHALLENGE_PATHS = [
  '/api/auth/login',
  '/api/auth/verify-otp',
  // The second step's own routes: a 401 means the sign-in in progress expired.
  '/api/auth/resend-2fa',
  '/api/auth/pending-2fa',
  // A mistyped / stale invite code also 401s (`auth/recovery.ts`). Same rule:
  // "those credentials were wrong", not "your session died".
  '/api/auth/accept-invite',
];

/**
 * The endpoint the subscriber re-checks to confirm an expiry.
 *
 * ⚠️ It answers **200 `{authenticated: false}`** when logged out — it does NOT
 * 401 (`auth/index.ts`: `if (!session) return { authenticated: false }`). Any
 * caller deciding "am I signed in?" must read the PAYLOAD; code that only
 * inspects rejection will silently conclude the session is alive, forever.
 */
export const AUTH_ME_PATH = '/api/auth/me';

/**
 * A 401 that MIGHT mean our session died — the subscriber confirms before
 * acting (see `AuthProvider`), because this alone is not sufficient evidence.
 *
 * 401 only, deliberately. The server's contract (`auth/access.ts`
 * `gateClientAccess`) is 401 = no session · 400 = bad clientId · **403 =
 * authenticated but not authorized for this clinic**. Treating 403 as expiry
 * would log a user out for visiting a clinic they simply lack access to.
 *
 * ⚠️ Why "might": `/api/kiosks/*` and `/api/documents/*` are `@fastify/http-proxy`
 * registrations that forward the UPSTREAM's status verbatim, and both upstreams
 * 401 on their own auth failures (ruby-control on a `control_session` mismatch;
 * document-manager on an invalid `document_session` — which `proxy.ts` emits
 * DELIBERATELY as `'invalid.no-secret'` when `DOCUMENT_MANAGER_SECRET` is
 * unset). Treating those as expiry would log an admin out of the whole portal
 * because a downstream service is misconfigured — and with the Kiosks page
 * polling, that becomes a login→401→logout loop. Hence the confirmation probe.
 */
export function isPossibleSessionExpiry(status: number, path: string): boolean {
  if (status !== 401) return false;
  return !AUTH_CHALLENGE_PATHS.some((p) => path.startsWith(p));
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { ...(options?.headers as Record<string, string>) };
  // JSON Content-Type for string bodies only — a FormData body must keep the
  // browser-set multipart Content-Type (with its boundary), so don't override it.
  if (options?.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }
  if (_selectedClinic) {
    headers['X-Client-Id'] = _selectedClinic;
  }
  // CSRF token for state-changing requests
  const method = options?.method?.toUpperCase() ?? 'GET';
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'same-origin',
    ...options,
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Notify BEFORE throwing: callers only ever see the ApiError, and most of
    // them swallow it into local error state, so this is the last point where
    // "the session might be gone" is still knowable.
    if (isPossibleSessionExpiry(res.status, path)) onSessionExpired?.();
    if (isIpRefusal(res.status, body)) onIpRefused?.(typeof body.ip === 'string' ? body.ip : null);
    throw new ApiError(res.status, body.error || `Request failed: ${res.status}`, body);
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return res.json();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Parsed JSON body from the error response. Useful when the server
     *  returns structured error details beyond `{ error: '...' }` — e.g.
     *  `LOCATION_INHERITS_VELOX` returns `{ error, locationId, inheritsFrom }`. */
    public details?: Record<string, unknown>,
  ) {
    super(message);
  }
}
